const STOP_WORDS = new Set([
  'the', 'of', 'and', 'a', 'an', 'chapter', 'section', 'story', 'context',
  'wa', 'al', 'ibn', 'bint'
])

const LETTER_OR_DIGIT_SEQUENCE = /[\p{L}\p{N}]+/gu
const PARENTHETICAL_CODE_PATTERN = /\(([^)]+)\)/u
const LEADING_CODE_PATTERN = /^\s*([A-Za-z][A-Za-z0-9]{1,7})(?:\s+[-:/|]\s*|\s*[:/|]\s*)/u
const WITNESS_CONTEXT_SIGLUM_PATTERN = /^\s*([A-Za-z]{2,6})(?=[\s._:/|-])/u
const STANDALONE_CONTEXT_SIGLUM_PATTERN = /^\s*([A-Za-z]{2,6})\s*$/u
const DEFAULT_LEDGER_PREFIX = 'Cx'
const LEGACY_FALLBACK_PREFIXES = new Set(['cx', 'ed', 'lo'])

const isLatinLetter = (value: string): boolean => /^[A-Za-z]$/.test(value)

const normalizePrefixCasing = (value: string): string => {
  const characters = Array.from(value).filter(character => character.trim().length > 0)
  if (characters.length === 0) {
    return 'Cx'
  }

  return characters
    .map((character, index) => {
      if (!isLatinLetter(character)) {
        return character
      }
      return index === 0 ? character.toUpperCase() : character.toLowerCase()
    })
    .join('')
}

const normalizeOptionalPrefix = (value: string | null | undefined): string | null => {
  if (typeof value !== 'string') {
    return null
  }
  const cleaned = value.trim().replace(/\s+/gu, '')
  if (cleaned.length === 0) {
    return null
  }
  const normalized = normalizePrefixCasing(cleaned)
  return normalized.length > 0 ? normalized : null
}

const isLegacyFallbackPrefix = (prefix: string | null | undefined): boolean => (
  Boolean(prefix && LEGACY_FALLBACK_PREFIXES.has(prefix.toLowerCase()))
)

const extractMeaningfulTokens = (title: string): string[] => {
  const tokens = title.match(LETTER_OR_DIGIT_SEQUENCE) || []
  const meaningfulTokens = tokens.filter(token => !STOP_WORDS.has(token.toLowerCase()))
  return meaningfulTokens.length > 0 ? meaningfulTokens : tokens
}

const buildBasePrefix = (tokens: string[]): string => {
  if (tokens.length >= 2) {
    return normalizePrefixCasing(`${Array.from(tokens[0])[0] || ''}${Array.from(tokens[1])[0] || ''}`)
  }
  if (tokens.length === 1) {
    return normalizePrefixCasing(Array.from(tokens[0]).slice(0, 2).join(''))
  }
  return 'Cx'
}

const buildCollisionCandidates = (tokens: string[], basePrefix: string): string[] => {
  if (tokens.length < 2) {
    const token = Array.from(tokens[0] || '')
    return token.slice(2).map((_, index) => normalizePrefixCasing(token.slice(0, index + 3).join('')))
  }

  const firstToken = Array.from(tokens[0] || '')
  const secondToken = Array.from(tokens[1] || '')
  const baseCharacters = Array.from(basePrefix)
  const candidates: string[] = []

  for (let index = 1; index < secondToken.length; index += 1) {
    candidates.push(normalizePrefixCasing([...baseCharacters, secondToken[index]].join('')))
  }
  for (let index = 1; index < firstToken.length; index += 1) {
    candidates.push(normalizePrefixCasing([...baseCharacters, firstToken[index]].join('')))
  }
  for (let index = 1; index < secondToken.length; index += 1) {
    for (let firstIndex = 1; firstIndex < firstToken.length; firstIndex += 1) {
      candidates.push(normalizePrefixCasing([...baseCharacters, secondToken[index], firstToken[firstIndex]].join('')))
    }
  }

  return Array.from(new Set(candidates.filter(candidate => candidate.length > 0)))
}

export const deriveContextLabelPrefix = (
  title: string,
  existingPrefixes: string[] = []
): string => {
  const trimmedTitle = title.trim()
  const normalizedExisting = new Set(existingPrefixes.map(prefix => prefix.trim().toLowerCase()).filter(Boolean))

  const explicitCode = trimmedTitle.match(PARENTHETICAL_CODE_PATTERN)?.[1]
    || trimmedTitle.match(LEADING_CODE_PATTERN)?.[1]
  if (explicitCode) {
    const normalizedExplicit = normalizePrefixCasing(explicitCode.replace(/[^\p{L}\p{N}]+/gu, ''))
    if (normalizedExplicit && !normalizedExisting.has(normalizedExplicit.toLowerCase())) {
      return normalizedExplicit
    }
  }

  const tokens = extractMeaningfulTokens(trimmedTitle)
  const basePrefix = buildBasePrefix(tokens)
  if (!normalizedExisting.has(basePrefix.toLowerCase())) {
    return basePrefix
  }

  const collisionCandidates = buildCollisionCandidates(tokens, basePrefix)
  const uniqueCandidate = collisionCandidates.find(candidate => !normalizedExisting.has(candidate.toLowerCase()))
  if (uniqueCandidate) {
    return uniqueCandidate
  }

  return basePrefix
}

export const inferContextPrefixFromWitnessSigla = (
  witnessSigla: Array<string | null | undefined> = []
): string | null => {
  const counts = new Map<string, number>()

  witnessSigla.forEach(siglum => {
    if (typeof siglum !== 'string') {
      return
    }
    const candidate = siglum.match(WITNESS_CONTEXT_SIGLUM_PATTERN)?.[1]
      || siglum.match(STANDALONE_CONTEXT_SIGLUM_PATTERN)?.[1]
    const normalized = normalizeOptionalPrefix(candidate)
    if (!normalized) {
      return
    }
    counts.set(normalized, (counts.get(normalized) || 0) + 1)
  })

  const ranked = Array.from(counts.entries()).sort((left, right) => {
    if (left[1] !== right[1]) {
      return right[1] - left[1]
    }
    return left[0].localeCompare(right[0])
  })
  const [best, next] = ranked
  if (!best) {
    return null
  }
  if (next && next[1] === best[1]) {
    return null
  }
  return best[0]
}

export interface LedgerLabelPrefixResolutionInput {
  contextLabelPrefix?: string | null
  contextTitle?: string | null
  secondaryPrefix?: string | null
  witnessSigla?: Array<string | null | undefined>
  existingPrefixes?: string[]
  fallbackPrefix?: string
}

export const resolveLedgerLabelPrefix = ({
  contextLabelPrefix,
  contextTitle,
  secondaryPrefix,
  witnessSigla = [],
  existingPrefixes = [],
  fallbackPrefix = DEFAULT_LEDGER_PREFIX
}: LedgerLabelPrefixResolutionInput): string => {
  const normalizedContextPrefix = normalizeOptionalPrefix(contextLabelPrefix)
  const normalizedSecondaryPrefix = normalizeOptionalPrefix(secondaryPrefix)
  const witnessContextPrefix = inferContextPrefixFromWitnessSigla(witnessSigla)

  if (witnessContextPrefix && (!normalizedContextPrefix || isLegacyFallbackPrefix(normalizedContextPrefix))) {
    return witnessContextPrefix
  }
  if (normalizedContextPrefix && !isLegacyFallbackPrefix(normalizedContextPrefix)) {
    return normalizedContextPrefix
  }
  if (witnessContextPrefix) {
    return witnessContextPrefix
  }
  if (normalizedSecondaryPrefix && !isLegacyFallbackPrefix(normalizedSecondaryPrefix)) {
    return normalizedSecondaryPrefix
  }
  if (normalizedContextPrefix) {
    return normalizedContextPrefix
  }
  if (typeof contextTitle === 'string' && contextTitle.trim().length > 0) {
    return deriveContextLabelPrefix(contextTitle, existingPrefixes)
  }
  if (normalizedSecondaryPrefix) {
    return normalizedSecondaryPrefix
  }
  return normalizeOptionalPrefix(fallbackPrefix) || DEFAULT_LEDGER_PREFIX
}
